import { useState } from 'react';
import type { ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
}

interface VariantStyle {
  bg: string;
  hover: string;
  press: string;
  color: string;
  border: string;
}
interface SizeStyle {
  h: number;
  px: number;
  fs: number;
}
type PressState = 'rest' | 'hover' | 'press';

const VARIANTS: Record<ButtonVariant, VariantStyle> = {
  primary: { bg: 'var(--primary)', hover: 'var(--primary-hover)', press: 'var(--primary-press)', color: 'var(--on-primary)', border: '1px solid transparent' },
  secondary: { bg: 'var(--primary-tint)', hover: 'rgba(204,115,87,.2)', press: 'rgba(204,115,87,.26)', color: 'var(--primary)', border: '1px solid var(--line-primary)' },
  ghost: { bg: 'transparent', hover: 'var(--surface-2)', press: 'var(--surface-3)', color: 'var(--text-1)', border: '1px solid var(--line-strong)' },
  danger: { bg: 'var(--danger-tint)', hover: 'rgba(240,86,74,.2)', press: 'rgba(240,86,74,.28)', color: 'var(--danger)', border: '1px solid var(--danger-line)' },
};
const SIZES: Record<ButtonSize, SizeStyle> = {
  sm: { h: 30, px: 12, fs: 12 },
  md: { h: 38, px: 16, fs: 13 },
  lg: { h: 48, px: 20, fs: 14.5 },
};

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
}: ButtonProps) {
  const [st, setSt] = useState<PressState>('rest');
  const v = VARIANTS[variant];
  const s = SIZES[size];
  const bg = disabled ? v.bg : st === 'press' ? v.press : st === 'hover' ? v.hover : v.bg;
  return (
    <button
      onClick={disabled || loading ? undefined : onClick}
      onMouseEnter={() => setSt('hover')}
      onMouseLeave={() => setSt('rest')}
      onMouseDown={() => setSt('press')}
      onMouseUp={() => setSt('hover')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: s.h,
        padding: '0 ' + s.px + 'px',
        width: fullWidth ? '100%' : undefined,
        background: bg,
        color: v.color,
        border: v.border,
        borderRadius: 'var(--r-md)',
        fontFamily: 'var(--font-body)',
        fontSize: s.fs,
        fontWeight: 600,
        lineHeight: 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'none',
      }}
    >
      {loading ? (
        <span
          style={{
            width: 13,
            height: 13,
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'tv-spin .7s linear infinite',
          }}
        />
      ) : (
        icon
      )}
      {children}
    </button>
  );
}
